import React, { useState, useEffect, useCallback } from "react";
import { doc, setDoc, collection, getFirestore, getDocs } from "firebase/firestore";
import { 
  Grid, 
  TextField, 
  Button, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Paper, 
  Chip,
  Typography,
  Box,
  Alert,
  CircularProgress,
  useTheme,
  IconButton,
  Card,
  CardContent,
  Divider,
  Avatar,
  Stack,
  InputAdornment,
  alpha,
  Fade,
  Zoom,
  Slide,
  Tooltip,
  Stepper,
  Step,
  StepLabel,
  StepContent,
  LinearProgress,
  TablePagination,
  Collapse,
  Menu,
  MenuItem,
  ListItemIcon,
  Badge
} from "@mui/material";
import { DateRange, AttachMoney, Save, TrendingUp, TrendingDown, Receipt, Payment, CheckCircle, Cancel, Print, Download, Share, Info, CalendarToday, Business, Percent, Euro, Description,PictureAsPdf,FileCopy, Assessment, PieChart as PieChartIcon, Refresh, FilterList, Clear } from "@mui/icons-material";
import { styled } from '@mui/material/styles';
import { motion, AnimatePresence } from 'framer-motion';
import { format, differenceInDays, isWithinInterval, parseISO } from 'date-fns';
import { fr } from 'date-fns/locale';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  BarChart,
  Bar
} from 'recharts';

const db = getFirestore();

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -20 }
};

const slideInRight = {
  initial: { opacity: 0, x: 30 },
  animate: { opacity: 1, x: 0 },
  exit: { opacity: 0, x: -30 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

// Styled Components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:nth-of-type(odd)': {
    backgroundColor: alpha(theme.palette.primary.main, 0.02),
  },
  '&:hover': {
    backgroundColor: alpha(theme.palette.primary.main, 0.05),
    transition: 'all 0.2s ease',
  },
  transition: 'all 0.2s ease',
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  padding: theme.spacing(1.5),
  borderBottom: `1px solid ${alpha(theme.palette.divider, 0.5)}`,
}));

const GradientCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main}05 0%, ${theme.palette.secondary.main}05 100%)`,
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  borderRadius: 16,
}));

const StatCard = ({ title, value, icon: Icon, color, subtitle, trend }) => {
  return (
    <motion.div
      whileHover={{ y: -5 }}
      transition={{ type: "spring", stiffness: 300 }}
    >
      <Card sx={{ 
        borderRadius: 3,
        background: 'white',
        border: `1px solid ${alpha(color, 0.2)}`,
        overflow: 'hidden',
        position: 'relative'
      }}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', letterSpacing: 1 }}>
                {title}
              </Typography>
              <Typography variant="h4" sx={{ fontWeight: 800, mt: 1, color }}>
                {value}
              </Typography>
              {subtitle && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {subtitle}
                </Typography>
              )}
              {trend && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 1 }}>
                  {trend > 0 ? (
                    <TrendingUp sx={{ fontSize: 14, color: '#10b981' }} />
                  ) : (
                    <TrendingDown sx={{ fontSize: 14, color: '#ef4444' }} />
                  )}
                  <Typography variant="caption" sx={{ color: trend > 0 ? '#10b981' : '#ef4444', fontWeight: 600 }}>
                    {Math.abs(trend)}% vs mois dernier
                  </Typography>
                </Box>
              )}
            </Box>
            <Avatar sx={{ bgcolor: alpha(color, 0.1), color }}>
              <Icon />
            </Avatar>
          </Box>
        </CardContent>
      </Card>
    </motion.div>
  );
};

const ExcelTablePartenaire = ({ partenairesData, encaissement }) => {
  const theme = useTheme();
  const [searchDateDebut, setSearchDateDebut] = useState("");
  const [searchDateFin, setSearchDateFin] = useState("");
  const [montantRecu, setMontantRecu] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [savedRanges, setSavedRanges] = useState([]);
  const [dateError, setDateError] = useState("");
  const [expandedRow, setExpandedRow] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [chartView, setChartView] = useState('area');
  const [anchorEl, setAnchorEl] = useState(null);
  const [showCharts, setShowCharts] = useState(true);

  // Load existing date ranges on mount
  useEffect(() => {
    const loadSavedRanges = async () => {
      try {
        const querySnapshot = await getDocs(collection(db, "MontantDateStatue"));
        const ranges = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
        setSavedRanges(ranges);
      } catch (err) {
        console.error("Erreur de chargement des périodes:", err);
      }
    };
    
    loadSavedRanges();
  }, []);

  // Memoize the date conflict check with partner ID
  const checkDateConflict = useCallback((start, end, partenaireId) => {
    if (!start || !end) return false;
    
    const newStart = new Date(start);
    const newEnd = new Date(end);
    
    if (newEnd < newStart) {
      return "La date de fin doit être après la date de début";
    }
    
    const hasConflict = savedRanges.some(range => {
      const rangeStart = new Date(range.DateDebut);
      const rangeEnd = new Date(range.DateFin);
      
      return (
        range.PartenaireId === partenaireId && (
          (newStart >= rangeStart && newStart <= rangeEnd) ||
          (newEnd >= rangeStart && newEnd <= rangeEnd) ||
          (newStart <= rangeStart && newEnd >= rangeEnd)
        )
      );
    });
    
    return hasConflict ? "Cette période chevauche une période déjà enregistrée pour ce partenaire" : "";
  }, [savedRanges]);

  // Handle date changes
  const handleDateChange = (type, value) => {
    if (type === "start") {
      setSearchDateDebut(value);
    } else {
      setSearchDateFin(value);
    }
    
    if (searchDateDebut && searchDateFin) {
      const partner = getCurrentPartner();
      const error = partner 
        ? checkDateConflict(
            type === "start" ? value : searchDateDebut,
            type === "end" ? value : searchDateFin,
            partner.id
          )
        : "Impossible de déterminer le partenaire pour cette période";
      
      setDateError(error);
    }
  };

  // Get current partner from filtered data
  const getCurrentPartner = () => {
    if (!filteredEncaissements.length) return null;
    const firstItem = filteredEncaissements[0]?.items[0];
    return partenairesData.find(p => 
      p.name.toLowerCase() === firstItem?.type?.toLowerCase()
    );
  };

  // Grouper et filtrer les encaissements
  const groupEncaissementsByDate = (encaissements) => {
    const grouped = encaissements.reduce((acc, curr) => {
      const date = curr.date;
      if (!acc[date]) acc[date] = [];
      acc[date].push(curr);
      return acc;
    }, {});
    
    return Object.entries(grouped).map(([date, items]) => ({
      date,
      items,
      totalValue: items.reduce((sum, item) => sum + item.value, 0),
      partner: partenairesData.find(p => p.name.toLowerCase() === items[0]?.type?.toLowerCase())
    }));
  };

  const allGroupedData = groupEncaissementsByDate(encaissement);
  
  const filteredEncaissements = allGroupedData
    .filter(({ date }) => {
      const dateObj = new Date(date);
      return (!searchDateDebut || dateObj >= new Date(searchDateDebut)) &&
             (!searchDateFin || dateObj <= new Date(searchDateFin));
    })
    .sort((a, b) => new Date(b.date) - new Date(a.date));

  // Pagination
  const paginatedData = filteredEncaissements.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  // Calculer les totaux
  const calculateFinancials = () => {
    let totalSales = 0;
    let totalCommission = 0;
    let totalVAT = 0;
    let totalNet = 0;
    
    filteredEncaissements.forEach(({ items, partner }) => {
      const dailyTotal = items.reduce((sum, item) => sum + item.value, 0);
      const commission = (dailyTotal * (partner?.commission || 0)) / 100;
      const vat = commission * 0.19;
      const net = dailyTotal - commission - vat;
      
      totalSales += dailyTotal;
      totalCommission += commission;
      totalVAT += vat;
      totalNet += net;
    });
    
    return {
      totalSales,
      totalCommission,
      totalVAT,
      totalNet,
      ecart: totalNet - (parseFloat(montantRecu) || 0)
    };
  };

  const financials = calculateFinancials();
  const ecart = financials.ecart;

  // Chart data preparation
  const chartData = filteredEncaissements.map(({ date, totalValue, partner }) => ({
    date: format(new Date(date), 'dd/MM'),
    sales: totalValue,
    commission: (totalValue * (partner?.commission || 0)) / 100,
    net: totalValue - ((totalValue * (partner?.commission || 0)) / 100) - (((totalValue * (partner?.commission || 0)) / 100) * 0.19)
  }));

  const pieData = [
    { name: 'Net à payer', value: financials.totalNet, color: '#10b981' },
    { name: 'Commission', value: financials.totalCommission, color: '#f59e0b' },
    { name: 'TVA', value: financials.totalVAT, color: '#ef4444' }
  ];

  // Gestionnaire d'enregistrement
  const handleSaveEntry = async () => {
    const partner = getCurrentPartner();
    if (!partner) {
      setError("Aucun partenaire trouvé pour cette période");
      return;
    }

    const conflictError = checkDateConflict(searchDateDebut, searchDateFin, partner.id);
    if (conflictError) {
      setDateError(conflictError);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const newEntry = {
        DateDebut: searchDateDebut,
        DateFin: searchDateFin,
        MontantRecu: parseFloat(montantRecu),
        TotalNet: financials.totalNet,
        Ecart: ecart,
        PartenaireId: partner.id,
        PartenaireNom: partner.name,
        createdAt: new Date().toISOString()
      };
      
      const docRef = doc(collection(db, "MontantDateStatue"));
      await setDoc(docRef, newEntry);
      setSavedRanges([...savedRanges, { ...newEntry, id: docRef.id }]);
      setSearchDateDebut("");
      setSearchDateFin("");
      setMontantRecu("");
      setSuccess("Paiement enregistré avec succès !");
      
      setTimeout(() => setSuccess(null), 3000);
    } catch (err) {
      setError("Erreur lors de l'enregistrement : " + err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleExport = (format) => {
    // Export logic would go here
    setSuccess(`Export ${format.toUpperCase()} initié`);
    setAnchorEl(null);
  };

  const isFormValid = () => {
    return searchDateDebut && 
           searchDateFin && 
           montantRecu && 
           !dateError &&
           parseFloat(montantRecu) > 0;
  };

  const COLORS = ['#10b981', '#f59e0b', '#ef4444', '#3b82f6', '#8b5cf6'];

  return (
    <Box sx={{ width: '100%' }}>
      <AnimatePresence>
        {success && (
          <Slide direction="down" in={true}>
            <Alert 
              severity="success" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setSuccess(null)}
              icon={<CheckCircle />}
            >
              {success}
            </Alert>
          </Slide>
        )}
        
        {error && (
          <Slide direction="down" in={true}>
            <Alert 
              severity="error" 
              sx={{ mb: 3, borderRadius: 2 }}
              onClose={() => setError(null)}
              icon={<Cancel />}
            >
              {error}
            </Alert>
          </Slide>
        )}
      </AnimatePresence>

      {/* Header Section */}
      <motion.div
        initial="initial"
        animate="animate"
        variants={fadeInUp}
      >
        <Paper sx={{ 
          p: 3, 
          mb: 3, 
          borderRadius: 3,
          background: `linear-gradient(135deg, ${alpha(theme.palette.primary.main, 0.05)} 0%, ${alpha(theme.palette.secondary.main, 0.05)} 100%)`,
          border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`
        }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 700, display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Receipt sx={{ color: theme.palette.primary.main }} />
                Réconciliation Financière
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Revue et réconciliation des transactions financières des partenaires
              </Typography>
            </Box>
            
            <Box sx={{ display: 'flex', gap: 1 }}>
              <Tooltip title="Exporter">
                <IconButton onClick={(e) => setAnchorEl(e.currentTarget)}>
                  <Download />
                </IconButton>
              </Tooltip>
              <Tooltip title="Imprimer">
                <IconButton>
                  <Print />
                </IconButton>
              </Tooltip>
              <Tooltip title="Partager">
                <IconButton>
                  <Share />
                </IconButton>
              </Tooltip>
              <Tooltip title="Afficher/Masquer graphiques">
                <IconButton onClick={() => setShowCharts(!showCharts)}>
                  <BarChart />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
        </Paper>
      </motion.div>

      {/* Stats Cards */}
      <motion.div
        variants={staggerContainer}
        initial="initial"
        animate="animate"
      >
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Chiffre d'affaires"
              value={`${financials.totalSales.toLocaleString()} TND`}
              icon={AttachMoney}
              color="#3b82f6"
              trend={12}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Commission totale"
              value={`${financials.totalCommission.toLocaleString()} TND`}
              icon={Percent}
              color="#f59e0b"
              trend={-5}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="TVA (19%)"
              value={`${financials.totalVAT.toLocaleString()} TND`}
              icon={Euro}
              color="#ef4444"
            />
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard
              title="Net à payer"
              value={`${financials.totalNet.toLocaleString()} TND`}
              icon={Payment}
              color="#10b981"
              subtitle="Après commissions et taxes"
            />
          </Grid>
        </Grid>
      </motion.div>

      {/* Charts Section */}
      <Collapse in={showCharts}>
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
        >
          <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Analyse financière
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                <Button
                  size="small"
                  variant={chartView === 'area' ? 'contained' : 'outlined'}
                  onClick={() => setChartView('area')}
                >
                  Évolution
                </Button>
                <Button
                  size="small"
                  variant={chartView === 'bar' ? 'contained' : 'outlined'}
                  onClick={() => setChartView('bar')}
                >
                  Comparatif
                </Button>
                <Button
                  size="small"
                  variant={chartView === 'pie' ? 'contained' : 'outlined'}
                  onClick={() => setChartView('pie')}
                >
                  Distribution
                </Button>
              </Box>
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={chartView === 'pie' ? 6 : 12}>
                <Box sx={{ height: 400 }}>
                  {chartView === 'area' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" stroke={alpha(theme.palette.divider, 0.5)} />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Area type="monotone" dataKey="sales" stackId="1" stroke="#3b82f6" fill={alpha('#3b82f6', 0.3)} name="Chiffre d'affaires" />
                        <Area type="monotone" dataKey="net" stackId="2" stroke="#10b981" fill={alpha('#10b981', 0.3)} name="Net à payer" />
                      </AreaChart>
                    </ResponsiveContainer>
                  )}
                  
                  {chartView === 'bar' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={chartData}>
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis dataKey="date" />
                        <YAxis />
                        <RechartsTooltip />
                        <Bar dataKey="sales" fill="#3b82f6" name="Chiffre d'affaires" />
                        <Bar dataKey="commission" fill="#f59e0b" name="Commission" />
                        <Bar dataKey="net" fill="#10b981" name="Net" />
                      </BarChart>
                    </ResponsiveContainer>
                  )}
                  
                  {chartView === 'pie' && (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                          outerRadius={120}
                          fill="#8884d8"
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                        <RechartsTooltip />
                        <Legend />
                      </PieChart>
                    </ResponsiveContainer>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      </Collapse>

      {/* Filters Section */}
      <motion.div variants={fadeInUp}>
        <Paper sx={{ p: 3, mb: 4, borderRadius: 3 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
            <FilterList fontSize="small" />
            Filtres de période
          </Typography>
          
          <Grid container spacing={2}>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                type="date"
                label="Date de début"
                variant="outlined"
                value={searchDateDebut}
                onChange={(e) => handleDateChange("start", e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                }}
                error={!!dateError}
                helperText={dateError}
              />
            </Grid>
            <Grid item xs={12} md={5}>
              <TextField
                fullWidth
                type="date"
                label="Date de fin"
                variant="outlined"
                value={searchDateFin}
                onChange={(e) => handleDateChange("end", e.target.value)}
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarToday fontSize="small" color="primary" />
                    </InputAdornment>
                  ),
                }}
                error={!!dateError}
              />
            </Grid>
            <Grid item xs={12} md={2}>
              <Button
                fullWidth
                variant="outlined"
                onClick={() => {
                  setSearchDateDebut("");
                  setSearchDateFin("");
                  setDateError("");
                }}
                startIcon={<Clear />}
                sx={{ height: '56px' }}
              >
                Réinitialiser
              </Button>
            </Grid>
          </Grid>
        </Paper>
      </motion.div>

      {/* Transactions Table */}
      <motion.div variants={fadeInUp}>
        <Paper sx={{ borderRadius: 3, overflow: 'hidden', mb: 4 }}>
          <Box sx={{ p: 2, borderBottom: `1px solid ${theme.palette.divider}`, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
              Transactions détaillées
            </Typography>
            <Chip 
              label={`${filteredEncaissements.length} transactions`} 
              size="small" 
              color="primary" 
              variant="outlined" 
            />
          </Box>
          
          <TableContainer>
            <Table>
              <TableHead sx={{ bgcolor: alpha(theme.palette.primary.main, 0.05) }}>
                <TableRow>
                  <StyledTableCell>Date</StyledTableCell>
                  <StyledTableCell>Partenaire</StyledTableCell>
                  <StyledTableCell align="right">Chiffre d'affaires</StyledTableCell>
                  <StyledTableCell align="right">Commission</StyledTableCell>
                  <StyledTableCell align="right">TVA</StyledTableCell>
                  <StyledTableCell align="right">Net à payer</StyledTableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                <AnimatePresence>
                  {paginatedData.map(({ date, items, totalValue, partner }) => {
                    const commission = (totalValue * (partner?.commission || 0)) / 100;
                    const vat = commission * 0.19;
                    const net = totalValue - commission - vat;
                    
                    return (
                      <StyledTableRow key={date}>
                        <StyledTableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Badge 
                              badgeContent={items.length} 
                              color="primary" 
                              sx={{ '& .MuiBadge-badge': { fontSize: 10, height: 18, minWidth: 18 } }}
                            >
                              <CalendarToday fontSize="small" color="action" />
                            </Badge>
                            <Typography variant="body2" fontWeight={500}>
                              {format(new Date(date), 'dd MMMM yyyy', { locale: fr })}
                            </Typography>
                          </Box>
                        </StyledTableCell>
                        <StyledTableCell>
                          <Chip
                            label={items[0]?.type}
                            size="small"
                            icon={<Business fontSize="small" />}
                            sx={{ bgcolor: alpha(theme.palette.primary.main, 0.1), color: theme.palette.primary.main }}
                          />
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <Typography fontWeight={600}>
                            {totalValue.toFixed(2)} TND
                          </Typography>
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: '#f59e0b' }}>
                          -{commission.toFixed(2)} TND
                        </StyledTableCell>
                        <StyledTableCell align="right" sx={{ color: '#ef4444' }}>
                          -{vat.toFixed(2)} TND
                        </StyledTableCell>
                        <StyledTableCell align="right">
                          <Chip
                            label={`${net.toFixed(2)} TND`}
                            size="small"
                            sx={{ bgcolor: alpha('#10b981', 0.1), color: '#10b981', fontWeight: 600 }}
                          />
                        </StyledTableCell>
                      </StyledTableRow>
                    );
                  })}
                </AnimatePresence>
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredEncaissements.length}
            rowsPerPage={rowsPerPage}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
            labelRowsPerPage="Lignes par page"
            labelDisplayedRows={({ from, to, count }) => `${from}-${to} sur ${count}`}
          />
        </Paper>
      </motion.div>

      {/* Settlement Section */}
      <motion.div variants={fadeInUp}>
        <GradientCard>
          <CardContent sx={{ p: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 3, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Payment />
              Récapitulatif de règlement
            </Typography>
            
            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Montant reçu (TND)"
                  variant="outlined"
                  type="number"
                  value={montantRecu}
                  onChange={(e) => setMontantRecu(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <AttachMoney color="primary" />
                      </InputAdornment>
                    ),
                    sx: { fontSize: '1.1rem' }
                  }}
                  helperText="Saisissez le montant effectivement reçu"
                />
              </Grid>
              
              <Grid item xs={12} md={6}>
                <Box sx={{ 
                  p: 2, 
                  borderRadius: 2, 
                  bgcolor: alpha(ecart >= 0 ? '#10b981' : '#ef4444', 0.05),
                  border: `1px solid ${alpha(ecart >= 0 ? '#10b981' : '#ef4444', 0.2)}`
                }}>
                  <Typography variant="body2" color="text.secondary" gutterBottom>
                    Différence de solde
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
                    <Typography variant="h4" sx={{ 
                      fontWeight: 700,
                      color: ecart >= 0 ? '#10b981' : '#ef4444'
                    }}>
                      {Math.abs(ecart).toFixed(3)} TND
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {ecart >= 0 ? 'à payer' : 'en trop'}
                    </Typography>
                  </Box>
                  {Math.abs(ecart) > 0 && (
                    <LinearProgress 
                      variant="determinate" 
                      value={Math.min(100, (Math.abs(ecart) / financials.totalNet) * 100)} 
                      sx={{ mt: 1, height: 4, borderRadius: 2 }}
                      color={ecart >= 0 ? 'success' : 'error'}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
            
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 3 }}>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  variant="contained"
                  onClick={handleSaveEntry}
                  disabled={!isFormValid() || loading}
                  size="large"
                  startIcon={loading ? <CircularProgress size={20} /> : <Save />}
                  sx={{ 
                    minWidth: 200,
                    py: 1.5,
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    background: `linear-gradient(135deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main})`,
                  }}
                >
                  {loading ? "Enregistrement..." : "Enregistrer le paiement"}
                </Button>
              </motion.div>
            </Box>
          </CardContent>
        </GradientCard>
      </motion.div>

      {/* Export Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={() => setAnchorEl(null)}
        TransitionComponent={Fade}
      >
        <MenuItem onClick={() => handleExport('excel')}>
          <ListItemIcon><Description fontSize="small" /></ListItemIcon>
          Exporter en Excel
        </MenuItem>
        <MenuItem onClick={() => handleExport('pdf')}>
          <ListItemIcon><PictureAsPdf fontSize="small" /></ListItemIcon>
          Exporter en PDF
        </MenuItem>
        <MenuItem onClick={() => handleExport('csv')}>
          <ListItemIcon><FileCopy fontSize="small" /></ListItemIcon>
          Exporter en CSV
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default ExcelTablePartenaire;